<template>
  <div class="user-profile-menu" ref="menuContainer">
    <!-- Menu Trigger -->
    <button 
      class="user-menu-trigger" 
      @click="toggleMenu"
      :aria-expanded="isOpen.toString()"
      aria-haspopup="true"
    >
      <div class="user-avatar-badge">
        {{ userInitial }}
      </div>
      <span class="user-display-name" v-if="showUserName">
        {{ currentUser?.unique_name || 'Guest' }}
      </span>
      <i class="bi bi-chevron-down menu-arrow" :class="{ rotated: isOpen }"></i>
    </button>

    <!-- User Menu -->
    <transition name="user-menu">
      <div v-show="isOpen" class="user-menu-panel" :class="{ 'panel-open': isOpen }">
        <!-- Profile Header -->
        <div class="user-profile-header">
          <div class="user-profile-info">
            <div class="user-avatar-badge large">
              {{ userInitial }}
            </div>
            <div class="user-profile-details">
              <h4 class="user-full-name">{{ displayName }}</h4>
              <p class="user-email-address">{{ currentUser?.email || 'No email' }}</p>
              <span v-if="currentUser?.Department" class="user-department-badge">
                {{ currentUser.Department.toUpperCase() }}
              </span>
              <span v-else class="user-department-badge">
                Citizen
              </span>
            </div>
          </div>
        </div>

        <div class="menu-divider"></div>

        <!-- Quick Actions -->
        <div class="menu-action-item" @click="navigateToProfile">
          <i class="bi bi-person"></i>
          <span>Profile Settings</span>
        </div>

        <!-- Conditional Scan Options -->
        <template v-if="currentUser">
          <div 
            v-if="hasRequiredRole(['admin', 'web-scan-live'])" 
            class="menu-action-item" 
            @click="handleScan('live')"
          >
            <i class="bi bi-qr-code-scan"></i>
            <span>Live Scan</span>
          </div>
          <div 
            v-if="hasRequiredRole(['admin', 'web-scan-print'])" 
            class="menu-action-item" 
            @click="handleScan('print')"
          >
            <i class="bi bi-printer"></i>
            <span>ID Print Scan</span>
          </div>
        </template>

        <div class="menu-divider"></div>

        <!-- Logout -->
        <div class="menu-action-item logout-action" @click="handleLogout">
          <i class="bi bi-box-arrow-left"></i>
          <span>Logout</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { getUserRoles } from "@/utils/auth"
// eslint-disable-next-line
const props = defineProps({
  showUserName: {
    type: Boolean,
    default: false
  }
})
// eslint-disable-next-line
const emit = defineEmits(['scan-toggle', 'profile-navigate','logout'])

const store = useStore()
const isOpen = ref(false)
const menuContainer = ref(null)

// Computed properties
const currentUser = computed(() => store.getters["auth/currentUser"] ?? null)
const userInitial = computed(() => currentUser.value?.unique_name?.[0]?.toUpperCase() || 'G')
const displayName = computed(() => currentUser.value?.unique_name?.toUpperCase() || "Guest")
// Methods
const toggleMenu = () => {
  isOpen.value = !isOpen.value
}

const closeMenu = () => {
  isOpen.value = false
}

const hasRequiredRole = (requiredRoles) => {
  return requiredRoles.some((role) => getUserRoles().includes(role))
}

const handleScan = (mode) => {
  emit('scan-toggle', mode)
  closeMenu()
}

const navigateToProfile = () => {
  emit('profile-navigate')
  closeMenu()
}

const handleLogout = async () => {
  
  emit('logout');
  closeMenu()
}

// Click outside to close
const handleClickOutside = (event) => {
  if (menuContainer.value && !menuContainer.value.contains(event.target)) {
    closeMenu()
  }
}

// Keyboard navigation
const handleKeydown = (event) => {
  if (!isOpen.value) return

  if (event.key === 'Escape') {
    closeMenu()
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.user-profile-menu {
  position: relative;
  display: inline-block;
}

/* Menu Trigger */
.user-menu-trigger {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 107, 107, 0.1);
  border: 2px solid transparent;
  border-radius: 16px;
  padding: 0.5rem 1rem;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.user-menu-trigger::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 107, 107, 0.1), transparent);
  transition: left 0.6s;
}

.user-menu-trigger:hover::before {
  left: 100%;
}

.user-menu-trigger:hover {
  background: rgba(179, 255, 107, 0.15);
  border-color: rgba(107, 255, 139, 0.3);
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(107, 255, 114, 0.15);
}

.user-menu-trigger:active {
  transform: translateY(0);
}

.user-avatar-badge {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6b6b, #ff8787);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  transition: all 0.3s ease;
}

.user-menu-trigger:hover .user-avatar-badge {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
}

.user-avatar-badge.large {
  width: 56px;
  height: 56px;
  font-size: 1.25rem;
  box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
}

.user-display-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #2c3e50;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menu-arrow {
  font-size: 0.8rem;
  color: #6c757d;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-arrow.rotated {
  transform: rotate(180deg);
  color: #6bffb5;
}

/* User Menu Panel */
.user-menu-panel {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.75rem;
  background: white;
  border-radius: 20px;
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 107, 107, 0.1);
  min-width: 300px;
  z-index: 1100;
  overflow: hidden;
  transform-origin: top right;
}

/* Menu Animation */
.user-menu-enter-active,
.user-menu-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.user-menu-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

.user-menu-enter-to {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.user-menu-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.user-menu-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

/* Profile Header */
.user-profile-header {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f5fff8 0%, #e6ffee 100%);
  position: relative;
  overflow: hidden;
}

.user-profile-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #6bffae, #b3ff87, #7cff6b);
  background-size: 200% 100%;
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { background-position: -200% 0; }
  50% { background-position: 200% 0; }
}

.user-profile-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-profile-details h4 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.2;
}

.user-email-address {
  margin: 0 0 0.75rem 0;
  color: #6c757d;
  font-size: 0.85rem;
  line-height: 1.3;
}

.user-department-badge {
  background: linear-gradient(135deg, #ff6b6b, #ff8787);
  color: white;
  padding: 0.375rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-block;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
}

/* Menu Divider */
.menu-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #f0f0f0, transparent);
  margin: 0.5rem 0;
}

/* Menu Action Items */
.menu-action-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  color: #495057;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  position: relative;
  overflow: hidden;
}

.menu-action-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 107, 107, 0.05), transparent);
  transition: left 0.5s;
}

.menu-action-item:hover::before {
  left: 100%;
}

.menu-action-item:hover {
  background: rgba(255, 107, 107, 0.03);
  color: #ff6b6b;
  padding-left: 1.75rem;
}

.menu-action-item i {
  width: 18px;
  font-size: 1rem;
  color: #6c757d;
  transition: color 0.3s ease;
}

.menu-action-item:hover i {
  color: #ff6b6b;
}

.menu-action-item span {
  font-weight: 500;
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

/* Logout Action */
.logout-action {
  color: #dc3545;
}

.logout-action:hover {
  color: #dc3545;
  background: rgba(220, 53, 69, 0.05);
}

.logout-action i {
  color: #dc3545;
}

/* Responsive Design */
@media (max-width: 768px) {
  .user-menu-trigger {
    padding: 0.5rem;
  }
  
  .user-display-name {
    display: none;
  }
  
  .user-menu-panel {
    position: fixed;
    top: 70px;
    left: 1rem;
    right: 1rem;
    min-width: auto;
    border-radius: 16px;
    z-index: 9999;
  }
  
  .user-profile-header {
    padding: 1.25rem;
  }
  
  .user-profile-info {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }
  
  .user-profile-details h4 {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .user-menu-panel {
    left: 0.5rem;
    right: 0.5rem;
  }
  
  .menu-action-item {
    padding: 0.875rem 1.25rem;
  }
}

/* Focus styles for accessibility */
.user-menu-trigger:focus-visible {
  outline: 2px solid #ff6b6b;
  outline-offset: 2px;
}

.menu-action-item:focus-visible {
  outline: 2px solid #ff6b6b;
  outline-offset: -2px;
  background: rgba(255, 107, 107, 0.05);
}

/* Ensure high z-index to appear above Bootstrap elements */
.user-profile-menu {
  z-index: 1100;
}

.user-menu-panel {
  z-index: 1101;
}
</style>